import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatEURCompact } from "@/lib/api";
import { toast } from "sonner";
import { ArrowsClockwise } from "@phosphor-icons/react";
import { PrioriteCard } from "@/components/commando/PrioriteCard";

const fr = (iso) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : "—");

// V11 — Commando intelligent (DA v2) : Mode du jour › Mes 3 priorités › Objectif mensuel
export default function CommandoV11({ refreshKey }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { setData((await api.get("/commando/v11")).data); } catch { toast.error("Commando indisponible"); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!data) return <div className="card-base p-5 text-sm text-slate-500" data-testid="c11-loading">Calcul des priorités…</div>;
  const { mode, priorites, objectif: o, queue_count } = data;
  const green = "var(--cf-primary)";
  return (
    <div className="space-y-4" data-testid="commando-v11">
      {/* BLOC 1 — MODE DU JOUR */}
      <section className="card-base p-4 md:p-5" data-testid="c11-mode">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: green }} data-testid="c11-mode-label">{mode.label}</span>
          <span className="text-[11px] text-slate-400">{mode.periode}</span>
          <button onClick={load} className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-slate-900" data-testid="c11-refresh"><ArrowsClockwise size={12} /> Actualiser</button>
        </div>
        <p className="text-base md:text-lg font-semibold text-slate-900 mt-1" data-testid="c11-mode-phrase">{mode.phrase}</p>
        <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-slate-600">
          <span data-testid="c11-mode-count"><b className="text-slate-900">{mode.dossiers_count}</b> dossier{mode.dossiers_count > 1 ? "s" : ""} concerné{mode.dossiers_count > 1 ? "s" : ""}</span>
          <span data-testid="c11-mode-ca"><b className="text-slate-900">{formatEURCompact(mode.ca_net_potentiel)}</b> de CA net potentiel</span>
          <span data-testid="c11-mode-echeance">Prochaine échéance <b className="text-slate-900">{fr(mode.prochaine_echeance.date)}</b> · <b style={{ color: green }}>{formatEURCompact(mode.prochaine_echeance.confirme)}</b> confirmé{mode.prochaine_echeance.projete > 0 ? ` · ${formatEURCompact(mode.prochaine_echeance.projete)} projeté` : ""}</span>
        </div>
      </section>

      {/* BLOC 2 — MES 3 PRIORITÉS */}
      <section data-testid="c11-priorites">
        <div className="flex items-center justify-between mb-2 px-1">
          <h2 className="text-base font-semibold text-slate-900">Mes 3 priorités</h2>
          {queue_count > 0 && <span className="text-[11px] text-slate-400" data-testid="c11-queue">+ {queue_count} en attente</span>}
        </div>
        {priorites.length === 0 ? (
          <div className="card-base p-5 text-sm text-slate-500" data-testid="c11-empty">Aucune action prioritaire : toutes les actions sont effectuées ou reportées.</div>
        ) : (
          <div className="space-y-2">{priorites.map((p, i) => <PrioriteCard key={p.action_id} p={p} index={i} onChanged={load} />)}</div>
        )}
      </section>

      {/* BLOC 3 — OBJECTIF MENSUEL */}
      <section className="card-base p-4 md:p-5" data-testid="c11-objectif">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="text-base font-semibold text-slate-900">Objectif mensuel</h2>
          <Link to="/pilotage" className="text-xs font-medium" style={{ color: green }} data-testid="c11-obj-detail">Voir le détail</Link>
        </div>
        <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 mt-2">
          <div><div className="text-[11px] text-slate-500">Réalisé</div><div className="text-2xl font-semibold" style={{ color: green }} data-testid="c11-obj-realise">{formatEURCompact(o.realise)}</div></div>
          <div><div className="text-[11px] text-slate-500">Objectif</div><div className="text-2xl font-semibold text-slate-900" data-testid="c11-obj-objectif">{formatEURCompact(o.objectif)}</div></div>
          <div><div className="text-[11px] text-slate-500">Restant</div><div className="text-2xl font-semibold text-slate-900" data-testid="c11-obj-restant">{formatEURCompact(o.restant)}</div></div>
          <div className="ml-auto text-right"><div className="text-[11px] text-slate-500">Rythme nécessaire</div><div className="text-sm font-semibold text-slate-900" data-testid="c11-obj-rythme">{formatEURCompact(o.rythme_quotidien)} / jour ouvré · {o.jours_ouvres_restants} j</div></div>
        </div>
        <div className="h-1.5 rounded-full bg-slate-100 mt-3 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.min(100, o.pct)}%`, background: green }} data-testid="c11-obj-progress" /></div>
        <div className="flex items-center justify-between mt-2 text-[11px] text-slate-400">
          <span data-testid="c11-obj-pct">{o.pct} % {o.atteint ? "· objectif atteint" : ""}</span>
          <span data-testid="c11-obj-mandats">Mandats {o.mandats.realises} / {o.mandats.objectif}</span>
        </div>
      </section>

      <div className="text-center pt-1">
        <Link to="/commando/recommandations" className="text-sm font-medium text-slate-500 hover:text-slate-900 underline-offset-4 hover:underline" data-testid="c11-all-link">Voir toutes les recommandations</Link>
      </div>
    </div>
  );
}
